import { NavLink, useLocation } from "react-router-dom";
import { PlusCircle, Users, TrendingUp, MessageSquareText, Store, Building2, Map, ShieldCheck, Headset, Activity } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { useDemoIdentity } from "../lib/DemoIdentityContext";
import { useTenantTerms } from "../lib/TenantConfigContext";
import { useRealtimeSync } from "../lib/useRealtimeSync";
import { DemoIdentitySwitcher } from "./DemoIdentitySwitcher";
import { AccountMenu, HoverSidebar } from "./AppShell";

// /intake-qr is intentionally not listed here - URL-only access, not linked from the UI.
// Live Status is retired — Customer Approval Pending, Pending Response, and Escalations
// are reached via cards on Overview instead of a nav item. Notifications is still a real
// route and component — just not linked from nav — see docs/backend-integration.md.
// `page` keys match roleViews.js's ROLE_VIEWS so nav visibility follows the same RBAC map
// the dashboard pages themselves read from.
export const ORGANIZATION = "Organization";

export const NAV = [
  { to: "/analytics", label: "Workshop Analytics", icon: TrendingUp, testid: "nav-analytics", page: "analytics", flagKey: "analytics" },
  { to: "/desk", label: "Advisor's Desk", icon: Headset, testid: "nav-desk", page: "desk", flagKey: "desk" },
  { to: "/chat-history", label: "Chat History", icon: MessageSquareText, testid: "nav-chat-history", page: "chatHistory", flagKey: "chat_history" },
  { to: "/create", label: "Book a Service", icon: PlusCircle, testid: "nav-create", page: "create", flagKey: "book_service" },
  // Only for a brand that has switched the Pre-IFR Journey on (requiresFlag).
  { to: "/pre-ifr", label: "Pre-IFR Agent", icon: Activity, testid: "nav-pre-ifr", page: "preIfr", flagKey: "pre_ifr", requiresFlag: "pre_ifr_journey" },
  { to: "/system-health", label: "System health", icon: Activity, testid: "nav-system-health", page: "systemHealth", flagKey: "system_health" },
  // The network and who works in it, grouped the way Super Admin groups it.
  // All of these are scoped by the API to what this login's access policy
  // already reaches.
  { to: "/regions", label: "Regions", icon: Map, testid: "nav-regions", page: "regions", flagKey: "organization", group: ORGANIZATION },
  { to: "/dealers", label: "Dealers", icon: Building2, testid: "nav-dealers", page: "dealers", flagKey: "organization", group: ORGANIZATION },
  { to: "/workshops", label: "Workshops", icon: Store, testid: "nav-workshops", page: "workshops", flagKey: "organization", group: ORGANIZATION },
  { to: "/people", label: "People", icon: Users, testid: "nav-people", page: "people", flagKey: "organization", group: ORGANIZATION },
  { to: "/roles", label: "Roles & access", icon: ShieldCheck, testid: "nav-roles", page: "roles", flagKey: "organization", group: ORGANIZATION },
];

const initials = (name) =>
  (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

// `fullBleed`: the page fills the viewport below the header (Service Desk),
// instead of the usual width-capped scrolling column.
// The nav this login actually gets: its role's permissions (Super Admin >
// Roles & access) narrowed by the brand's Feature access toggles.
export function useVisibleNav() {
  const { role } = useDemoIdentity();
  const { isPageEnabled, canSeePage, featureFlags } = useTenantTerms();
  return NAV.filter((item) => {
    const allowed = canSeePage(role, item.page);
    const switchedOn = !item.requiresFlag || featureFlags?.[item.requiresFlag] === true;
    return allowed && switchedOn && isPageEnabled(item.flagKey);
  });
}

export const Layout = ({ children, fullBleed = false }) => {
  const loc = useLocation();
  const { me, signOut } = useAuth();
  const { tenant, roleLabel } = useTenantTerms();
  const visibleNav = useVisibleNav();
  useRealtimeSync();

  const toItem = ({ to, label, icon: Icon, testid }) => ({
    key: to,
    to,
    testid,
    label,
    icon: <Icon strokeWidth={1.9} />,
    active: loc.pathname === to || loc.pathname.startsWith(`${to}/`),
  });
  // Ungrouped items stay at the top under no heading; the rest fall under
  // theirs, in the order they appear in NAV.
  const groups = [];
  visibleNav.forEach((item) => {
    const label = item.group || "";
    const existing = groups.find((g) => g.label === label);
    if (existing) existing.items.push(toItem(item));
    else groups.push({ label, accent: label === ORGANIZATION ? "#2F9E44" : undefined, items: [toItem(item)] });
  });

  return (
    <div className="flex min-h-screen bg-white">
      <HoverSidebar
        groups={groups}
        renderItem={(it, { className, content }) => (
          <NavLink key={it.key} to={it.to} data-testid={it.testid} aria-label={it.label} className={className}>
            {content}
          </NavLink>
        )}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="relative z-30 flex h-[72px] flex-none items-center justify-between gap-4 border-b border-[#F2F4F7] px-3 sm:px-6 lg:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-11 w-11 flex-none items-center justify-center overflow-hidden rounded-xl bg-white text-[14px] font-semibold text-[#4B5563] shadow-[0_0_0_1px_#E5E7EB]">
              {tenant?.logo_url ? (
                <img src={tenant.logo_url} alt="" className="h-full w-full object-contain p-0.5" />
              ) : (
                initials(tenant?.name) || "–"
              )}
            </span>
            <span className="truncate text-[18px] font-semibold text-[#111827]" data-testid="brand-name">
              {tenant?.name || ""}
            </span>
          </div>

          <div className="flex flex-none items-center gap-3">
            <DemoIdentitySwitcher />
            {me && <AccountMenu email={me.email} subtitle={roleLabel(me.role)} onSignOut={signOut} />}
          </div>
        </header>

        {fullBleed ? (
          <main className="h-[calc(100vh-72px)] min-h-0 w-full px-3 pb-4 pt-4 sm:px-6 lg:px-8">{children}</main>
        ) : (
          <main className="w-full max-w-7xl flex-1 px-3 pb-8 pt-6 sm:px-6 lg:px-8">{children}</main>
        )}
      </div>
    </div>
  );
};
